import BarChartIcon from '@mui/icons-material/BarChart';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PublicIcon from '@mui/icons-material/Public';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { DASHBOARD_WIDGETS, QUICK_STATS_CODE } from '@app/shared';
import type {
  AgentsDataRow,
  DashboardWidgetKey,
  InactiveReasonRow,
  MasterAgentRow,
  PeriodCounts,
  QuickStatsDto,
  SalesDataRow,
} from '@app/shared';
import { usePageTitle } from '../../app/pageTitle';
import { useShellColours } from '../../app/BrandTheme';
import { tokens } from '../../app/theme';
import { ErrorState, LoadingState } from '../../components/PageState';
import { formatCents } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import {
  useActivations,
  useAgentsData,
  useByMasterAgent,
  useInactiveByReason,
  useQuickStats,
  useSalesData,
  useSubscribersByState,
} from './api';

const count = (n: number) => n.toLocaleString('en-US');
/** Rows a table widget shows before "More...". */
const PREVIEW_ROWS = 5;
const BOX_HEIGHT = 475;
const CHART_HEIGHT = 380;

/* -------------------------------- Quick Stats ------------------------------ */

function InfoBox(props: {
  title: string;
  color: string;
  icon: ReactNode;
  lines: Array<[label: string, value: string]>;
}) {
  return (
    <Paper variant="outlined" sx={{ display: 'flex', minHeight: 90 }} aria-label={props.title}>
      <Box
        sx={{
          width: 90,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: props.color,
          color: '#fff',
        }}
      >
        {props.icon}
      </Box>
      <Box sx={{ px: 1.5, py: 0.75 }}>
        <Typography sx={{ fontSize: 14 }}>{props.title}</Typography>
        {props.lines.map(([label, value]) => (
          <Typography key={label} sx={{ fontSize: 13, fontWeight: 700 }}>
            {label} {value}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
}

function SmallBox(props: { label: string; value: string; color: string }) {
  return (
    <Box sx={{ bgcolor: props.color, color: '#fff', borderRadius: 0.5, px: 1.5, py: 1.5 }}>
      <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1.1 }}>{props.value}</Typography>
      <Typography sx={{ fontSize: 11 }}>{props.label}</Typography>
    </Box>
  );
}

const periods = (c: PeriodCounts): Array<[string, string]> => [
  ['Last 1 Week', count(c.week)],
  ['Last 1 Month', count(c.month)],
  ['Last 1 Year', count(c.year)],
];

function QuickStats({ stats }: { stats: QuickStatsDto }) {
  const grid = {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
    mb: 2,
  };
  return (
    <Box component="section" aria-label="Quick Stats">
      <Typography component="h2" sx={{ fontSize: 30, textAlign: 'center', mb: 2 }}>
        {stats.loginId}&apos;s Stats
      </Typography>
      <Box sx={grid}>
        <InfoBox
          title="Sales"
          color={tokens.stat.green}
          icon={<ShoppingCartIcon fontSize="large" />}
          lines={periods(stats.sales)}
        />
        <InfoBox
          title="Activation Status"
          color={tokens.stat.aqua}
          icon={<CheckIcon fontSize="large" />}
          lines={periods(stats.activations)}
        />
        <InfoBox
          title="Deactivation"
          color={tokens.stat.red}
          icon={<CloseIcon fontSize="large" />}
          lines={periods(stats.deactivations)}
        />
        <InfoBox
          title="Available Inventory"
          color={tokens.stat.yellow}
          icon={<Inventory2Icon fontSize="large" />}
          lines={[
            ['SIMs', count(stats.availableInventory.sims)],
            ['CDMA', count(stats.availableInventory.cdma)],
            ['GSM', count(stats.availableInventory.gsm)],
          ]}
        />
      </Box>
      <Box sx={grid}>
        <SmallBox
          label="Your Today's Sales"
          value={count(stats.today.sales)}
          color={tokens.stat.green}
        />
        <SmallBox
          label="Your Today's Disconnection"
          value={count(stats.today.disconnections)}
          color={tokens.stat.red}
        />
        <SmallBox
          label="Your Today's Payments Excluding Tax"
          value={formatCents(stats.today.paymentsExTaxCents)}
          color={tokens.stat.yellow}
        />
        <SmallBox
          label="Your Today's Used Inventory"
          value={count(stats.today.usedInventory)}
          color={tokens.stat.aqua}
        />
      </Box>
    </Box>
  );
}

/* --------------------------------- Widgets -------------------------------- */

interface TableColumn<T> {
  header: string;
  value: (row: T) => string;
  align?: 'right';
}

function RowsTable<T>(props: {
  label: string;
  columns: TableColumn<T>[];
  rows: T[];
  numbered?: boolean;
}) {
  return (
    <TableContainer>
      <Table aria-label={props.label}>
        <TableHead>
          <TableRow>
            {props.numbered ? <TableCell>#</TableCell> : null}
            {props.columns.map((c) => (
              <TableCell key={c.header} align={c.align}>
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.rows.map((row, i) => (
            <TableRow key={i} hover>
              {props.numbered ? <TableCell>{i + 1}</TableCell> : null}
              {props.columns.map((c) => (
                <TableCell key={c.header} align={c.align} sx={{ whiteSpace: 'nowrap' }}>
                  {c.value(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {props.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={props.columns.length + (props.numbered ? 1 : 0)} align="center">
                No data available.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** A table widget: the first rows, then "More..." opening the whole table in a dialog. */
function TableWidget<T>(props: { title: string; columns: TableColumn<T>[]; rows: T[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <RowsTable
        label={props.title}
        columns={props.columns}
        rows={props.rows.slice(0, PREVIEW_ROWS)}
      />
      {props.rows.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="outlined" onClick={() => setOpen(true)}>
            More...
          </Button>
        </Box>
      ) : null}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{props.title}</DialogTitle>
        <DialogContent dividers>
          <RowsTable
            label={`${props.title}, all rows`}
            columns={props.columns}
            rows={props.rows}
            numbered
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const money = <T,>(header: string, cents: (row: T) => number): TableColumn<T> => ({
  header,
  value: (row) => formatCents(cents(row)),
  align: 'right',
});
const number = <T,>(header: string, n: (row: T) => number): TableColumn<T> => ({
  header,
  value: (row) => count(n(row)),
  align: 'right',
});

interface Loaded<T> {
  data: T | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => unknown;
}

/** Loading and error states shared by every widget body. */
function Body<T>({ query, children }: { query: Loaded<T>; children: (data: T) => ReactNode }) {
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.isPending || query.data === undefined) return <LoadingState />;
  return <>{children(query.data)}</>;
}

function StateChart() {
  const query = useSubscribersByState(true);
  return (
    <Body query={query}>
      {(rows) =>
        rows.length === 0 ? (
          <Typography sx={{ p: 2 }}>No data available.</Typography>
        ) : (
          // The reference draws a 3D donut; the chart library here has no 3D.
          <PieChart
            height={CHART_HEIGHT}
            series={[
              {
                data: rows.map((r) => ({ id: r.state, value: r.subscribers, label: r.name })),
                innerRadius: 70,
                paddingAngle: 1,
                arcLabel: (item) => String(item.label),
                arcLabelMinAngle: 18,
                valueFormatter: (item) => `Subscribers: ${count(item.value)}`,
              },
            ]}
            slotProps={{ legend: { hidden: true } }}
            // White labels stay readable on every slice colour.
            sx={{ '& .MuiPieArcLabel-root': { fill: '#fff', fontSize: 13 } }}
          />
        )
      }
    </Body>
  );
}

function ActivationChart() {
  const query = useActivations(true);
  return (
    <Body query={query}>
      {(data) => (
        <BarChart
          height={CHART_HEIGHT}
          xAxis={[{ scaleType: 'band', data: data.months.map((m) => m.label) }]}
          yAxis={[{ label: 'Count', tickMinStep: 1 }]}
          series={[
            {
              data: data.months.map((m) => m.activations),
              label: 'Activation',
              stack: 'total',
              color: tokens.chart.activation,
            },
            {
              data: data.months.map((m) => m.deactivations),
              label: 'Deactivation',
              stack: 'total',
              color: tokens.chart.deactivation,
            },
          ]}
        />
      )}
    </Body>
  );
}

function MasterAgentTable({ title }: { title: string }) {
  const query = useByMasterAgent(true);
  return (
    <Body query={query}>
      {(rows) => (
        <TableWidget
          title={title}
          rows={rows}
          columns={[
            { header: 'Master Agent', value: (r) => r.masterAgent },
            number<MasterAgentRow>('Subscriber', (r) => r.subscriber),
            number<MasterAgentRow>('Active', (r) => r.active),
            number<MasterAgentRow>('Inactive', (r) => r.inactive),
            number<MasterAgentRow>('Suspended', (r) => r.suspended),
            number<MasterAgentRow>('Dispatched', (r) => r.dispatched),
            number<MasterAgentRow>('Others', (r) => r.others),
          ]}
        />
      )}
    </Body>
  );
}

function ReasonTable({ title }: { title: string }) {
  const query = useInactiveByReason(true);
  return (
    <Body query={query}>
      {(rows) => (
        <TableWidget
          title={title}
          rows={rows}
          columns={[
            { header: 'Reason', value: (r) => r.reason },
            number<InactiveReasonRow>('Total Subscriber', (r) => r.total),
          ]}
        />
      )}
    </Body>
  );
}

function SalesTable({ title }: { title: string }) {
  const query = useSalesData(true);
  return (
    <Body query={query}>
      {(rows) => (
        <TableWidget
          title={title}
          rows={rows}
          columns={[
            { header: 'Payment By', value: (r) => r.paymentBy },
            money<SalesDataRow>('Sum Of Total Amount', (r) => r.totalAmountCents),
            money<SalesDataRow>('Sum Of Equipment Purchase', (r) => r.equipmentCents),
            money<SalesDataRow>('Sum Of Plan Cost', (r) => r.planCostCents),
            money<SalesDataRow>('Sum Of Plan Cost Tax', (r) => r.planCostTaxCents),
            money<SalesDataRow>('Sum Of Total Plan Cost', (r) => r.totalPlanCostCents),
            money<SalesDataRow>('Sum Of Activation Fee', (r) => r.activationFeeCents),
            money<SalesDataRow>('Sum Of Activation Fee Tax', (r) => r.activationFeeTaxCents),
            money<SalesDataRow>('Sum Of Equipment Purchase Tax', (r) => r.equipmentTaxCents),
            money<SalesDataRow>('Sum Of Shipping Charge', (r) => r.shippingCents),
            money<SalesDataRow>('Sum Of Late Fee Charge', (r) => r.lateFeeCents),
            money<SalesDataRow>('Sum Of Restore Fee Charge', (r) => r.restoreFeeCents),
            money<SalesDataRow>('Sum Of Regulation Fee', (r) => r.regulationFeeCents),
            money<SalesDataRow>('Sum Of Restore Fee Charge Tax', (r) => r.restoreFeeTaxCents),
            money<SalesDataRow>('Sum Of Discount', (r) => r.discountCents),
          ]}
        />
      )}
    </Body>
  );
}

function AgentsTable({ title }: { title: string }) {
  const query = useAgentsData(true);
  return (
    <Body query={query}>
      {(rows) => (
        <TableWidget
          title={title}
          rows={rows}
          columns={[
            { header: 'Payment By', value: (r) => r.paymentBy },
            money<AgentsDataRow>('Product Sales', (r) => r.productSalesCents),
            money<AgentsDataRow>('New Line Sales', (r) => r.newLineSalesCents),
            money<AgentsDataRow>('Shipping Charged', (r) => r.shippingChargedCents),
            money<AgentsDataRow>('Daily Total', (r) => r.dailyTotalCents),
            money<AgentsDataRow>('Month To Date', (r) => r.monthToDateCents),
            money<AgentsDataRow>('Year To Date', (r) => r.yearToDateCents),
          ]}
        />
      )}
    </Body>
  );
}

const WIDGET_ICON: Record<DashboardWidgetKey, ReactNode> = {
  'subscribers-by-state': <PublicIcon sx={{ fontSize: 96 }} />,
  activations: <BarChartIcon sx={{ fontSize: 96 }} />,
  'by-master-agent': <GroupsIcon sx={{ fontSize: 96 }} />,
  'inactive-by-reason': <PersonOffIcon sx={{ fontSize: 96 }} />,
  sales: <PaymentsIcon sx={{ fontSize: 96 }} />,
  agents: <SupportAgentIcon sx={{ fontSize: 96 }} />,
};

function WidgetBody({ widget, title }: { widget: DashboardWidgetKey; title: string }) {
  switch (widget) {
    case 'subscribers-by-state':
      return <StateChart />;
    case 'activations':
      return <ActivationChart />;
    case 'by-master-agent':
      return <MasterAgentTable title={title} />;
    case 'inactive-by-reason':
      return <ReasonTable title={title} />;
    case 'sales':
      return <SalesTable title={title} />;
    default:
      return <AgentsTable title={title} />;
  }
}

/** One dashboard box: covered by "Click to view" until the user asks for it. */
function WidgetBox(props: { widget: DashboardWidgetKey; title: string; code: string }) {
  const colours = useShellColours();
  const [open, setOpen] = useState(false);
  // The reference puts the current year straight after this title.
  const name =
    props.widget === 'activations' ? `${props.title}${new Date().getFullYear()}` : props.title;
  const heading = `${name} (${props.code})`;
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={heading}
      sx={{
        // The cover needs the reference's box height; a loaded widget takes what it needs.
        minHeight: open ? undefined : BOX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        gridColumn: props.widget === 'sales' ? '1 / -1' : undefined,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: tokens.cardHeader,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Typography variant="h2" component="h2">
          {heading}
        </Typography>
      </Box>
      {open ? (
        <Box sx={{ p: 1.5, minWidth: 0 }}>
          <WidgetBody widget={props.widget} title={props.title} />
        </Box>
      ) : (
        <ButtonBase
          onClick={() => setOpen(true)}
          aria-label={`Click for view ${props.title}`}
          sx={{
            flexGrow: 1,
            flexDirection: 'column',
            gap: 1,
            color: colours.topBar,
          }}
        >
          {WIDGET_ICON[props.widget]}
          <Typography sx={{ fontSize: 20, color: tokens.cardTitle }}>Click to view</Typography>
        </ButtonBase>
      )}
    </Paper>
  );
}

/**
 * Dashboard, the home page (reference appendix 11.1). Each widget needs its own permission
 * code and loads only when asked for, as on the reference.
 */
export function DashboardPage() {
  const { can } = useAuth();
  usePageTitle('Dashboard');
  const [statsAsked, setStatsAsked] = useState(false);
  const stats = useQuickStats(statsAsked);
  const widgets = DASHBOARD_WIDGETS.filter((w) => can(w.code));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, minHeight: 34 }}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>
          Dashboard
        </Typography>
        {can(QUICK_STATS_CODE) && !statsAsked ? (
          <Button variant="contained" onClick={() => setStatsAsked(true)}>
            Load Quick Stats ({QUICK_STATS_CODE})
          </Button>
        ) : null}
      </Box>

      {statsAsked ? (
        stats.error ? (
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        ) : stats.data ? (
          <QuickStats stats={stats.data} />
        ) : (
          <LoadingState />
        )
      ) : null}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {widgets.map((w) => (
          <WidgetBox key={w.key} widget={w.key} title={w.title} code={w.code} />
        ))}
      </Box>
    </Box>
  );
}
